import type { ReactNode } from 'react';
import { Badge, statusTone } from './Badge.js';
import { Button } from './Button.js';
import { cn } from '../cn.js';
import { initials } from '../initials.js';

export interface ApprovalCardProps {
  /** e.g. "Leave request" — the entity type, in the reader's language. */
  kind: string;
  requester: { name: string; avatarUrl?: string | null; subtitle?: string };
  /** Label/value pairs: dates, amount, reason. Pre-formatted and localised by the caller. */
  facts: Array<{ label: string; value: ReactNode }>;
  status: string;
  statusLabel: string;
  /** Where this instance is in its chain, e.g. "Step 2 of 3 · Finance". */
  stepLabel?: string;
  /** Absent when the viewer may only observe — an approval they are not the approver for. */
  onApprove?: () => void;
  onReject?: () => void;
  busy?: boolean;
  /**
   * Marks a card the AI proposed. Every AI write is a proposal a human approves, and the reader
   * must be able to see that this originated with the agent rather than a colleague — that
   * distinction is what an EU AI Act audit asks for, and what stops the agent's suggestions being
   * mistaken for a manager's instruction.
   */
  proposedByAi?: boolean;
  /**
   * Rendered between the facts and the actions — what one surface needs beside the decision
   * itself (the inbox's reject-comment box and its "Open" link) without forking the card.
   */
  children?: ReactNode;
  className?: string;
}

/**
 * The universal approval unit.
 *
 * ONE component, rendered identically in a chat thread, the approvals inbox, a notification, and
 * the mobile home screen — and resolvable from any of them. That is a product decision as much as
 * a UI one: approvals are the main thing managers do in an HRMS, and making them act in whichever
 * surface they are already looking at is most of the "chat-first HR" claim.
 *
 * Building it per-surface instead is how the four copies drift, and how "approve from Slack" ends
 * up behaving subtly differently from "approve from the inbox".
 */
export function ApprovalCard({
  kind,
  requester,
  facts,
  status,
  statusLabel,
  stepLabel,
  onApprove,
  onReject,
  busy = false,
  proposedByAi = false,
  children,
  className,
}: ApprovalCardProps) {
  const actionable = Boolean(onApprove || onReject);

  return (
    <article
      className={cn(
        'rounded-lg border border-border bg-surface p-4 shadow-sm',
        'max-w-lg', // a chat thread is narrow; the inbox should not stretch this to full width
        className,
      )}
    >
      <header className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5">
          {requester.avatarUrl ? (
            <img
              src={requester.avatarUrl}
              alt=""
              className="size-8 rounded-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="flex size-8 items-center justify-center rounded-full bg-bg-subtle text-xs font-medium text-fg-muted">
              {initials(requester.name)}
            </div>
          )}
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-fg">{requester.name}</p>
            <p className="truncate text-xs text-fg-muted">{requester.subtitle ?? kind}</p>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-1.5">
          {proposedByAi && <Badge tone="accent">AI</Badge>}
          {/* Colour is never the only signal — the label always accompanies the tone. */}
          <Badge tone={statusTone(status)}>{statusLabel}</Badge>
        </div>
      </header>

      <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
        {facts.map((fact) => (
          <div key={fact.label} className="contents">
            <dt className="text-fg-muted">{fact.label}</dt>
            <dd className="text-fg">{fact.value}</dd>
          </div>
        ))}
      </dl>

      {stepLabel && (
        <p className="mt-3 border-t border-border pt-2 text-xs text-fg-subtle">{stepLabel}</p>
      )}

      {children}

      {actionable && (
        <footer className="mt-4 flex items-center gap-2">
          {onApprove && (
            <Button variant="primary" size="sm" onClick={onApprove} loading={busy}>
              Approve
            </Button>
          )}
          {onReject && (
            <Button variant="secondary" size="sm" onClick={onReject} disabled={busy}>
              Reject
            </Button>
          )}
        </footer>
      )}
    </article>
  );
}
